import { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment/moment";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import { createApolloClient } from "../../providers/ApolloClientFactory";
import {
  GET_EVENT_DEVICES_UNION,
  GET_EVENT_DEVICE_ORDERS,
} from "../devices/queries";
import { UNASSIGN_DEVICE } from "../devices/mutations";

const client = createApolloClient();

interface AssignedDevice {
  id: number;
  mac_address: string;
  manufacturer: string | null;
  model_number: string | null;
  internal_part_number: string | null;
  android_device_id: string | null;
  license_plate: string | null;
  deployment_status: string | null;
  current_possession: string | null;
  deployed_event_name: string | null;
  deployed_at: string | null;
  updated_at: string | null;
  assigned_event_id: number | null;
  active_event_id: number | null;
  session_order_count: number | null;
}

interface EventOrder {
  android_device_id: string | null; // the device's license plate, despite the name
  created_at: string;
  transaction_at: string | null;
  build_id: string | null;
}

type Verdict = "Online" | "Idle" | "Stale" | "Offline" | "Unknown";

type SortKey = "mac" | "identity" | "seen" | "order" | "status";

// posapp and posapp-s700 have independent versionCode counters (posapp is in
// the 500s from build 500 on, s700 moves to the 600s), so build numbers are
// only comparable within the same app family.
// The family is inferred from the device's hardware model.
const appFamilyFor = (d: { model_number: string | null } | undefined): string => {
  if (!d?.model_number) return "Unknown app";
  return d.model_number.toUpperCase().includes("S700") ? "S700" : "POS";
};

const verdictFor = (heartbeatAt: string | null | undefined): Verdict => {
  if (!heartbeatAt) return "Unknown";
  const minutes = moment().diff(moment(heartbeatAt), "minutes", true);
  if (minutes < 5) return "Online";
  if (minutes < 60) return "Idle";
  if (minutes < 60 * 24) return "Stale";
  return "Offline";
};

const verdictPill = (v: Verdict) => {
  switch (v) {
    case "Online": return "bg-green-50 text-green-700";
    case "Idle":   return "bg-gray-100 text-gray-700";
    case "Stale":  return "bg-amber-50 text-amber-700";
    case "Offline":return "bg-red-50 text-red-700";
    default:       return "bg-gray-100 text-gray-600";
  }
};

const fromNow = (iso: string | null | undefined) => (iso ? moment(iso).fromNow() : "—");

export const EventDevicesSection = ({
  eventId,
  fromISO,
  toISO,
}: {
  eventId: number;
  fromISO?: string | null;
  toISO?: string | null;
}) => {
  const [assigned, setAssigned] = useState<AssignedDevice[]>([]);
  const [orderByPlate, setOrderByPlate] = useState<Record<string, EventOrder>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Orders first: they define which license plates sold at this event,
      // which the devices query then folds into its union.
      const ordersRes = await client.query({
        query: GET_EVENT_DEVICE_ORDERS,
        variables: {
          eventId,
          from: fromISO || "1970-01-01T00:00:00Z",
          to: toISO || "2100-01-01T00:00:00Z",
        },
        fetchPolicy: "network-only",
      });
      const orders = ((ordersRes.data?.orders ?? []) as EventOrder[]).filter(
        (o) => o.android_device_id?.trim()
      );
      const plates = orders.map((o) => o.android_device_id!.trim());

      const devicesRes = await client.query({
        query: GET_EVENT_DEVICES_UNION,
        variables: { eventId, plates },
        fetchPolicy: "network-only",
      });
      const devs = (devicesRes.data?.devices ?? []) as AssignedDevice[];

      // Plates that sold but have no device row at all: synthesize a stub so
      // the register still shows up in the list.
      const knownPlates = new Set(
        devs.map((d) => d.license_plate?.trim()).filter(Boolean)
      );
      const stubs: AssignedDevice[] = plates
        .filter((p) => !knownPlates.has(p))
        .map((p, i) => ({
          id: -(i + 1),
          mac_address: "",
          manufacturer: null,
          model_number: null,
          internal_part_number: null,
          android_device_id: null,
          license_plate: p,
          deployment_status: null,
          current_possession: null,
          deployed_event_name: null,
          deployed_at: null,
          updated_at: null,
          assigned_event_id: null,
          active_event_id: null,
          session_order_count: null,
        }));

      setAssigned([...devs, ...stubs]);
      setOrderByPlate(
        Object.fromEntries(orders.map((o) => [o.android_device_id!.trim(), o]))
      );
    } catch (e: any) {
      setError(e?.message ?? "Failed to load event devices");
      setAssigned([]);
      setOrderByPlate({});
    } finally {
      setLoading(false);
    }
  }, [eventId, fromISO, toISO]);

  useEffect(() => { refresh(); }, [refresh]);

  const removeDevice = async (id: number) => {
    try {
      await client.mutate({ mutation: UNASSIGN_DEVICE, variables: { id } });
      setAssigned((prev) => prev.filter((d) => d.id !== id));
      toast.success("Device removed from event");
    } catch (e: any) {
      toast.error("Failed to remove: " + (e?.message ?? ""));
    }
  };

  const orderFor = useCallback(
    (d: AssignedDevice) =>
      d.license_plate ? orderByPlate[d.license_plate.trim()] : undefined,
    [orderByPlate]
  );

  // A device's freshest proof of life: its heartbeat OR its latest order at
  // this event, whichever is newer. Selling devices often don't heartbeat and
  // heartbeating devices often don't sell, so neither alone is honest.
  const lastSignalFor = useCallback(
    (d: AssignedDevice): string | null => {
      const o = orderFor(d);
      if (!d.updated_at) return o?.created_at ?? null;
      if (!o) return d.updated_at;
      return moment(o.created_at).isAfter(d.updated_at) ? o.created_at : d.updated_at;
    },
    [orderFor]
  );

  // ===== Sorting =====
  const [sortKey, setSortKey] = useState<SortKey>("seen");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const onSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    } else {
      setSortKey(key);
      // Timestamps read best newest-first; text columns A→Z.
      setSortDir(key === "seen" || key === "order" ? "desc" : "asc");
    }
  };

  const rows = useMemo(() => {
    const verdictRank: Record<Verdict, number> = {
      Online: 0, Idle: 1, Stale: 2, Offline: 3, Unknown: 4,
    };
    const sortValue = (d: AssignedDevice): string | number | null => {
      switch (sortKey) {
        case "mac":
          return (d.mac_address || d.license_plate || "").toLowerCase() || null;
        case "identity":
          return (
            [d.manufacturer, d.model_number, d.internal_part_number]
              .filter(Boolean)
              .join(" ")
              .toLowerCase() || null
          );
        case "order":
          return orderFor(d)?.created_at ?? null;
        case "status":
          return verdictRank[verdictFor(lastSignalFor(d))];
        case "seen":
        default:
          return lastSignalFor(d);
      }
    };
    const dir = sortDir === "asc" ? 1 : -1;
    return [...assigned].sort((a, b) => {
      const va = sortValue(a);
      const vb = sortValue(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1; // missing values always sink to the bottom
      if (vb == null) return -1;
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [assigned, sortKey, sortDir, orderFor, lastSignalFor]);

  // ===== Health summary =====
  const summary = useMemo(() => {
    const counts = { total: assigned.length, online: 0, idle: 0, stale: 0, offline: 0, unknown: 0 };
    let assignedCount = 0;
    let liveCount = 0;
    let sellingCount = 0;
    let lateOrders = 0;
    for (const d of assigned) {
      const v = verdictFor(lastSignalFor(d));
      if (v === "Online") counts.online++;
      else if (v === "Idle") counts.idle++;
      else if (v === "Stale") counts.stale++;
      else if (v === "Offline") counts.offline++;
      else counts.unknown++;

      if (d.assigned_event_id === eventId) assignedCount++;
      if (d.active_event_id === eventId) liveCount++;
      const o = orderFor(d);
      if (o) sellingCount++;
      if (o?.transaction_at && moment(o.created_at).diff(moment(o.transaction_at), "minutes") > 60) {
        lateOrders++;
      }
    }
    return { ...counts, assignedCount, liveCount, sellingCount, lateOrders };
  }, [assigned, eventId, orderFor, lastSignalFor]);

  // App builds in use, from each selling device's most recent order, grouped
  // by app family (posapp vs posapp-s700 number their builds independently,
  // so a cross-family difference is NOT a device being behind).
  //
  // A given build number belongs to exactly ONE of the two apps, but device
  // hardware records are patchy — so each build's family is decided by a vote
  // of the devices running it that DO have a known model, and that verdict is
  // applied to the whole build cohort. One correctly-registered S700 on build
  // 472 classifies every device on 472, including stubs and bad QB records.
  const buildsInUse = useMemo(() => {
    const deviceByPlate = new Map(
      assigned
        .filter((d) => d.license_plate?.trim())
        .map((d) => [d.license_plate!.trim(), d])
    );

    const votes = new Map<string, Record<string, number>>();
    const totals = new Map<string, number>();
    for (const [plate, o] of Object.entries(orderByPlate)) {
      const build = o.build_id?.trim() || "unknown";
      totals.set(build, (totals.get(build) ?? 0) + 1);
      const family = appFamilyFor(deviceByPlate.get(plate));
      if (family === "Unknown app") continue;
      const tally = votes.get(build) ?? {};
      tally[family] = (tally[family] ?? 0) + 1;
      votes.set(build, tally);
    }

    return Array.from(totals.entries())
      .map(([build, devices]) => {
        const tally = votes.get(build);
        const family = tally
          ? Object.entries(tally).sort((a, b) => b[1] - a[1])[0][0]
          : "Unknown app";
        return { family, build, devices };
      })
      .sort((a, b) => a.family.localeCompare(b.family) || b.devices - a.devices);
  }, [assigned, orderByPlate]);

  // Only a spread WITHIN one app family means someone is on an old build.
  const familiesWithMixedBuilds = useMemo(() => {
    const perFamily = new Map<string, Set<string>>();
    for (const b of buildsInUse) {
      if (b.family === "Unknown app") continue;
      if (!perFamily.has(b.family)) perFamily.set(b.family, new Set());
      perFamily.get(b.family)!.add(b.build);
    }
    return Array.from(perFamily.entries())
      .filter(([, builds]) => builds.size > 1)
      .map(([family]) => family);
  }, [buildsInUse]);

  return (
    <section className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="flex w-full items-center justify-between px-5 py-4 border-b border-gray-100 text-left hover:bg-gray-50 transition-colors"
      >
        <div>
          <h2 className="text-base font-semibold text-gray-900">Devices</h2>
          <div className="text-xs text-gray-500">
            {summary.total} device{summary.total === 1 ? "" : "s"}
            {` · ${summary.assignedCount} assigned`}
            {` · ${summary.liveCount} live`}
            {` · ${summary.sellingCount} selling`}
            {summary.online > 0 ? ` · ${summary.online} online` : ""}
            {summary.offline > 0 ? ` · ${summary.offline} offline` : ""}
            {summary.lateOrders > 0 ? ` · ${summary.lateOrders} arrived late` : ""}
          </div>
        </div>
        <svg
          viewBox="0 0 20 20"
          fill="currentColor"
          className={`h-5 w-5 shrink-0 text-gray-400 transition-transform ${expanded ? "rotate-90" : ""}`}
        >
          <path
            fillRule="evenodd"
            d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {error && (
        <div className="bg-red-50 border-b border-red-200 text-red-700 px-4 py-3 text-sm">{error}</div>
      )}

      {expanded && !loading && buildsInUse.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 px-5 py-2.5 border-b border-gray-100 bg-gray-50/60">
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            Builds in use
          </span>
          {buildsInUse.map(({ family, build, devices }) => (
            <span
              key={`${family}|${build}`}
              className={`rounded px-2 py-0.5 text-xs font-medium ${
                build === "unknown"
                  ? "bg-gray-100 text-gray-500"
                  : family === "S700"
                  ? "bg-purple-50 text-purple-800"
                  : "bg-blue-50 text-blue-800"
              }`}
            >
              {family}
              {" · "}
              {build === "unknown" ? "unknown build" : `build ${build}`}
              {" · "}
              {devices} device{devices === 1 ? "" : "s"}
            </span>
          ))}
          {familiesWithMixedBuilds.length > 0 && (
            <span className="text-xs text-amber-700">
              mixed {familiesWithMixedBuilds.join(" & ")} builds — some devices
              are behind
            </span>
          )}
        </div>
      )}

      {!expanded ? null : loading ? (
        <div className="text-center py-10 text-gray-400 text-sm">Loading devices...</div>
      ) : assigned.length === 0 ? (
        <div className="text-center py-10 text-gray-400 text-sm">
          No devices are assigned to, live at, or selling at this event yet.
        </div>
      ) : (
        <>
          <div className="hidden md:grid grid-cols-12 gap-2 px-5 py-2 bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wide border-b border-gray-100">
            {(
              [
                { key: "mac", label: "MAC", span: "col-span-3" },
                { key: "identity", label: "Identity", span: "col-span-3" },
                { key: "seen", label: "Last seen", span: "col-span-2" },
                { key: "order", label: "Last order", span: "col-span-2" },
                { key: "status", label: "Status", span: "col-span-1" },
              ] as { key: SortKey; label: string; span: string }[]
            ).map((col) => (
              <button
                key={col.key}
                type="button"
                onClick={() => onSort(col.key)}
                className={`${col.span} flex items-center gap-1 text-left uppercase tracking-wide hover:text-gray-800 transition-colors`}
              >
                {col.label}
                {sortKey === col.key && (
                  <span className="text-gray-400">{sortDir === "desc" ? "↓" : "↑"}</span>
                )}
              </button>
            ))}
            <div className="col-span-1 text-right"></div>
          </div>
          <div className="divide-y divide-gray-100">
            {rows.map((d) => {
              const v = verdictFor(lastSignalFor(d));
              const o = orderFor(d);
              const isStub = d.id < 0;
              const lateDelta =
                o?.transaction_at && o?.created_at
                  ? moment(o.created_at).diff(moment(o.transaction_at), "minutes")
                  : null;
              const arrivedLate = lateDelta != null && lateDelta > 60;
              const identity = [d.manufacturer, d.model_number, d.internal_part_number]
                .filter(Boolean)
                .join(" · ");
              const label = d.mac_address || (d.license_plate ? `#${d.license_plate}` : `#${d.id}`);
              return (
                <div key={d.id} className="grid grid-cols-1 md:grid-cols-12 gap-2 px-5 py-3 hover:bg-gray-50 transition-colors items-center text-sm">
                  <div className="col-span-3 min-w-0">
                    {isStub ? (
                      <span className="font-medium text-gray-900 truncate inline-block max-w-full">
                        {label}
                      </span>
                    ) : (
                      <Link
                        to={`/devices/${d.id}`}
                        className="font-medium text-gray-900 hover:text-red-700 truncate inline-block max-w-full"
                      >
                        {label}
                      </Link>
                    )}
                    {d.active_event_id === eventId && (
                      <span className="ml-1.5 rounded bg-green-100 px-1.5 py-px text-[10px] font-semibold text-green-700 align-middle">
                        LIVE
                      </span>
                    )}
                    {isStub && (
                      <span className="ml-1.5 rounded bg-gray-100 px-1.5 py-px text-[10px] font-semibold text-gray-500 align-middle">
                        UNREGISTERED
                      </span>
                    )}
                    {d.license_plate && d.mac_address && (
                      <div className="text-xs text-gray-400">#{d.license_plate}</div>
                    )}
                  </div>
                  <div className="col-span-3 text-gray-600 text-xs truncate">{identity || "—"}</div>
                  <div className="col-span-2 text-gray-600 text-xs">{fromNow(lastSignalFor(d))}</div>
                  <div className="col-span-2 text-gray-600 text-xs">
                    {fromNow(o?.created_at)}
                    {o?.build_id && (
                      <div className="text-xs text-gray-400">build {o.build_id}</div>
                    )}
                    {arrivedLate && (
                      <div className="text-xs text-amber-700">
                        late by {moment.duration(lateDelta!, "minutes").humanize()}
                      </div>
                    )}
                  </div>
                  <div className="col-span-1">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${verdictPill(v)}`}>{v}</span>
                  </div>
                  <div className="col-span-1 text-right">
                    {d.assigned_event_id === eventId && (
                      <button
                        type="button"
                        onClick={() => removeDevice(d.id)}
                        className="text-xs text-gray-400 hover:text-red-700 transition-colors"
                        title="Remove from event"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </section>
  );
};
